import React, { useState, useRef, useEffect } from 'react';
import styles from './style.module.scss';
import Modal from '../../../components/modal';
import Button from '../../../components/new-button';
import TextInput from '../../../components/form/text-input';
import Switch from '../../../components/form/switch'
import { Formik, Form } from "formik";
import * as Yup from "yup";
import GeneratedSign from '../../../pages/create-app/components/application-form/components/review-submit/components/sign-agreement/component/generated-sign';
import ReactSignatureCanvas from "react-signature-canvas";
import Select from '../../../components/form/select';
import { US_STATES } from "../../../../../js/constants";
import { updateUserProfile } from '../../../../api';
import { toast } from 'react-hot-toast';
import useUserStore, { fetchUserProfile } from '../../../stores/user';

const US_STATE_LIST = Object.entries(US_STATES).map(([_, v]) => ({
  label: v,
  value: v,
}));


const validationSchema = Yup.object().shape({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  direct_phone: Yup.string().required("Direct phone is required"),
  cell_phone: Yup.string().required("Cell phone is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.object().required("State is required"),
  zip_code: Yup.string().required("Zip code is required"),
});


function PersonalDetails({ isOpen, onClose }) {

  const userProfile = useUserStore((state) => state.userProfile);
  const setPhoneVerificationModal = useUserStore(
    (state) => state.setPhoneVerificationModal,
  );
  // Master switch (config/otp.php). Off means the server saves the number straight
  // from this form, so there is nothing to hand to the verification modal.
  const phoneOtpEnabled = useUserStore(
    (state) => state.user?.phone_verification_enabled !== false,
  );

  const [activeTab, setActiveTab] = useState("select-style");
  const [signedImages, setSignedImages] = useState({
    signedName: "",
    signedInitials: "",
  });
  const [signedName, setSignedName] = useState("");
  const [signedInitials, setSignedInitials] = useState("");
  const [signatureImage, setSignatureImage] = useState("");
  const [isSigned, setIsSigned] = useState(false);

  const [updateSignature, setUpdateSignature] = useState(false);

  // ref for signature canvas
  const sigPad = useRef(null);

  const handleSubmit = async (values, formikBag) => {
    const { setSubmitting } = formikBag || {};

    // The cell number is the login second factor, so the server drops it from this
    // form. A change here is handed to the verification modal instead, and only
    // takes effect once the user types the code we text to the new number.
    const digitsOf = (v) => String(v || "").replace(/\D/g, "");
    const phoneChanged =
      phoneOtpEnabled && digitsOf(values.cell_phone) !== digitsOf(userProfile?.cell_phone);

    try {
      const response = await updateUserProfile({
        ...values,
        update_signature: updateSignature,
        signature: activeTab === "draw-signature" ? signatureImage : signedImages.signedName,
      });
      if (response.success) {
        // The server drops phone_cell from this form, so a changed number has NOT
        // saved here — it lands when the code texted to it comes back. The generic
        // "profile updated" would report a change that has not happened, and the
        // number reverts if the user closes the modal without finishing.
        toast.success(
          phoneChanged
            ? "Profile saved. Verify your new number to finish changing it."
            : response.message,
        );
        fetchUserProfile();
      } else {
        toast.error(response.message);
      }
      onClose();
      if (phoneChanged) {
        setPhoneVerificationModal(true, digitsOf(values.cell_phone));
      }
    } catch (error) {
      console.error("Error updating personal details:", error);
    } finally {
      if (setSubmitting) {
        setSubmitting(false);
      }
    }
  };

  const createSiganture = () => {
    const signature =
      Object.keys(sigPad.current).length !== 0 && !sigPad.current.isEmpty()
        ? sigPad.current.getTrimmedCanvas().toDataURL("image/png")
        : "";
    setSignatureImage(signature);
  };

  const handleSignedImages = async () => {
    try {
      // signedName, signedInitials
      if (signedName.length === 1) {
        setIsSigned(false);
        setSignatureImage("");
        setSignedImages({
          signedName: "",
          signedInitials: "",
        });
        return;
      }
      // Define the font and other styles
      const fontSize = 70;
      const fontFamily = "Adobe Handwriting Ernie";
      const font = `${fontSize}px ${fontFamily}`;
      const text_color = "black";

      // Measure the text widths for signedName and signedInitials
      const tempCanvas = document.createElement("canvas");
      const tempContext = tempCanvas.getContext("2d");
      tempContext.font = font;
      const nameWidth = tempContext.measureText(signedName).width;
      const initialsWidth = tempContext.measureText(signedInitials).width;

      // Determine the maximum width for both canvases
      let maxWidth = Math.max(nameWidth, initialsWidth) + 35;
      let minWidth = 300;
      if (maxWidth < minWidth) {
        maxWidth = minWidth;
      }

      // Create a canvas for the name with the maximum width
      const nameCanvas = document.createElement("canvas");
      nameCanvas.width = maxWidth;
      const nameContext = nameCanvas.getContext("2d");

      // Create a canvas for the initials with the maximum width
      const initialsCanvas = document.createElement("canvas");
      initialsCanvas.width = maxWidth;
      const initialsContext = initialsCanvas.getContext("2d");

      // Calculate positions
      const hasNameSpecialCharacter = signedName.match(/[fgjpqyz]/i);
      const hasInitialSpecialCharacter = signedInitials.match(/[fgjpqyz]/i);
      const x = 25;
      const nameY = hasNameSpecialCharacter ? 80 : 100;
      const initialY = hasInitialSpecialCharacter ? 80 : 100;

      // Draw the name on the name canvas
      nameContext.font = font;
      nameContext.fillStyle = text_color;
      nameContext.fillText(signedName, x, nameY);

      // Draw the initials on the initials canvas
      initialsContext.font = font;
      initialsContext.fillStyle = text_color;
      initialsContext.fillText(signedInitials, x, initialY);

      setSignedImages({
        signedName: nameCanvas ? nameCanvas.toDataURL("image/png") : "",
        signedInitials: initialsCanvas
          ? initialsCanvas.toDataURL("image/png")
          : "",
      });
      setSignatureImage(nameCanvas ? nameCanvas.toDataURL("image/png") : "");
      setIsSigned(true);
    } catch (err) {
      console.log("error in handleSignedImages", err);
      setIsSigned(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, [isOpen]);

  useEffect(() => {
    handleSignedImages();
  }, [signedName]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} header="Change Personal Details" size="md">

      <Formik
        initialValues={userProfile}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, isSubmitting, handleChange, values }) => (
          <Form>
            <div className={styles.personal_details_body}>
              <div className={`${styles.grid} ${styles.gridCol2} ${styles.gap16px}`} >
                <TextInput
                  label="First Name"
                  placeholder="First Name"
                  id="first_name"
                  name="first_name"
                  value={values.first_name}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  error={errors.first_name}
                  errored={errors.first_name}
                  touched={touched.first_name}
                />
                <TextInput
                  label="Last Name"
                  placeholder="Last Name"
                  id="last_name"
                  name="last_name"
                  value={values.last_name}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  error={errors.last_name}
                  errored={errors.last_name}
                  touched={touched.last_name}
                />
              </div>

              <div className={`${styles.grid} ${styles.gridCol3} ${styles.gap16px}`} >
                <TextInput
                  label="Direct Phone"
                  placeholder="Direct Phone"
                  id="direct_phone"
                  name="direct_phone"
                  value={values.direct_phone}
                  onChange={handleChange}
                  error={errors.direct_phone}
                  errored={errors.direct_phone}
                  touched={touched.direct_phone}
                  cleaveOptions={{
                    blocks: [3, 3, 4],
                    delimiter: "-",
                    numericOnly: true,
                    onValueChanged: (e) => {
                      handleChange({
                        target: { name: "direct_phone", value: e.target.value },
                      });
                    },
                  }}
                />
                <TextInput
                  label="Cell Phone"
                  placeholder="Cell Phone"
                  id="cell_phone"
                  name="cell_phone"
                  value={values.cell_phone}
                  onChange={handleChange}
                  error={errors.cell_phone}
                  errored={errors.cell_phone}
                  touched={touched.cell_phone}
                  cleaveOptions={{
                    blocks: [3, 3, 4],
                    delimiter: "-",
                    numericOnly: true,
                    onValueChanged: (e) => {
                      handleChange({
                        target: { name: "cell_phone", value: e.target.value },
                      });
                    },
                  }}
                  hasWarning={
                    phoneOtpEnabled
                      ? "Changing this sends a code to the new number — it applies once you enter that code."
                      : ""
                  }
                />
                <TextInput
                  label="Email"
                  placeholder="Email"
                  id="email"
                  name="email"
                  value={values.email}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  error={errors.email}
                  errored={errors.email}
                  touched={touched.email}
                />
              </div>

              <div className={`${styles.grid} ${styles.gridCol1} ${styles.gap16px}`} >
                <TextInput
                  label="Address"
                  placeholder="Address"
                  id="address"
                  name="address"
                  value={values.address}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  error={errors.address}
                  errored={errors.address}
                  touched={touched.address}
                />
              </div>

              <div className={`${styles.grid} ${styles.gridCol3} ${styles.gap16px}`} >
                <TextInput
                  label="City"
                  placeholder="City"
                  id="city"
                  name="city"
                  value={values.city}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  error={errors.city}
                  errored={errors.city}
                  touched={touched.city}
                />
                <Select
                  label="State"
                  placeholder="Select State"
                  id="state"
                  name="state"
                  options={US_STATE_LIST}
                  value={values.state || ""}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      value = { label: value, value: value };
                    } else {
                      value = value;
                    }
                    handleChange({ target: { name: "state", value: value } });
                  }}
                  error={errors.state}
                  errored={errors.state}
                  touched={touched.state}
                  search={true}
                />
                <TextInput
                  label="Zip Code"
                  placeholder="Zip Code"
                  id="zip_code"
                  name="zip_code"
                  value={values.zip_code}
                  onChange={handleChange}
                  error={errors.zip_code}
                  errored={errors.zip_code}
                  touched={touched.zip_code}
                  cleaveOptions={{
                    blocks: [5],
                    delimiter: "",
                    numericOnly: true,
                    onValueChanged: (e) => {
                      handleChange({
                        target: { name: "zip_code", value: e.target.value },
                      });
                    },
                  }}
                />
              </div>

              <Switch
                label="Update Signature"
                name="update_signature"
                checked={updateSignature}
                value={updateSignature}
                onChange={(e) => {
                  setUpdateSignature(!updateSignature);
                  setSignedName(values.signature_first_name + " " + values.signature_last_name);
                  setSignedInitials(values.signature_first_name?.charAt(0) + values.signature_last_name?.charAt(0));
                }}
              />


              {/* Tabs */}
              {updateSignature && (
                <div className={styles.signatureContainer}>
                  <div className={styles.tabGroup}>
                    <button
                      className={`${styles.tabItem} ${activeTab === "select-style" ? styles.active : ""}`}
                      onClick={() => setActiveTab("select-style")}
                      type="button"
                    >
                      Select Style
                    </button>
                    <button
                      className={`${styles.tabItem} ${activeTab === "draw-signature" ? styles.active : ""}`}
                      onClick={() => setActiveTab("draw-signature")}
                      type="button"
                    >
                      Draw Signature
                    </button>
                  </div>

                  {activeTab === "select-style" && (
                    <div className={styles.selectStyleContainer}>
                      <div className={`${styles.grid} ${styles.gridCol2} ${styles.gap16px}`} >
                        <TextInput
                          label="Signature First Name"
                          placeholder="Signature First Name"
                          id="signature_first_name"
                          name="signature_first_name"
                          value={values.signature_first_name}
                          onChange={(e) => {
                            handleChange(e);
                            setSignedName(e.target.value + " " + values.signature_last_name);
                            setSignedInitials(e.target.value?.charAt(0) + values.signature_last_name?.charAt(0));
                          }}
                          error={errors.signature_first_name}
                          errored={errors.signature_first_name}
                          touched={touched.signature_first_name}
                        />
                        <TextInput
                          label="Signature Last Name"
                          placeholder="Signature Last Name"
                          id="signature_last_name"
                          name="signature_last_name"
                          value={values.signature_last_name}
                          onChange={(e) => {
                            handleChange(e);
                            setSignedName(values.signature_first_name + " " + e.target.value);
                            setSignedInitials(values.signature_first_name?.charAt(0) + e.target.value?.charAt(0));
                          }}
                          error={errors.signature_last_name}
                          errored={errors.signature_last_name}
                          touched={touched.signature_last_name}
                        />
                      </div>
                      <GeneratedSign signedImages={signedImages} />
                    </div>
                  )}

                  {activeTab === "draw-signature" && (
                    <div className={styles.drawSignatureContainer}>
                      <ReactSignatureCanvas
                        penColor="black"
                        canvasProps={{
                          // width: 1200,
                          // height: 150,
                          className: styles.signatureCanvas,
                        }}
                        ref={sigPad}
                        onEnd={() => {
                          setIsSigned(true);
                          createSiganture();
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

            </div>

            <div className={styles.personal_details_footer}>
              <Button
                onClick={onClose}
                disabled={isSubmitting}
                variant="secondary"
                type="button"
              >
                <span>Cancel</span>
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                isLoading={isSubmitting}
                variant="primary"
              >
                <span>Submit</span>
              </Button>
            </div>
          </Form>
        )}
      </Formik>

    </Modal>
  )
}

export default PersonalDetails